import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';
import { FilePlus2, RefreshCw } from 'lucide-react';

const PagesManagementModal = ({ isOpen, onClose, embedded = false }) => {
  const [pages, setPages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPage, setEditingPage] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  // Форма для новой/редактируемой страницы
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    title: '',
    icon: '',
    link: '',
    category: '',
    displayOrder: 0,
    isActive: true
  });

  useEffect(() => {
    if (isOpen) {
      fetchPages();
      fetchCategories();
    }
  }, [isOpen]);

  const fetchPages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pages/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPages(data);
      }
    } catch (error) {
      console.error('Ошибка загрузки страниц:', error);
      setError('Ошибка загрузки страниц');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/page-categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Ошибка загрузки категорий:', error);
    }
  };

  const handleAddPage = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchPages();
        setShowAddForm(false);
        resetForm();
        setError('');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Ошибка при создании страницы');
      }
    } catch (error) {
      setError('Ошибка при создании страницы');
    }
  };

  const handleUpdatePage = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pages/${editingPage}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchPages();
        setEditingPage(null);
        resetForm();
        setError('');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Ошибка при обновлении страницы');
      }
    } catch (error) {
      setError('Ошибка при обновлении страницы');
    }
  };

  const handleDeletePage = async (pageId) => {
    if (!window.confirm('Вы уверены, что хотите деактивировать эту страницу?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pages/${pageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchPages();
        setError('');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Ошибка при удалении страницы');
      }
    } catch (error) {
      setError('Ошибка при удалении страницы');
    }
  };

  const handleEditPage = (page) => {
    console.log('Editing page:', page);
    setEditingPage(page.id);

    // Находим категорию страницы
    let pageCategory = '';
    Object.entries(pages).forEach(([categoryName, categoryPages]) => {
      const foundPage = categoryPages.find(p => p.id === page.id);
      if (foundPage) {
        // Находим id категории по имени
        const category = categories.find(c => c.name === categoryName);
        if (category) {
          pageCategory = category.id;
        }
      }
    });

    setFormData({
      id: page.id,
      name: page.name,
      title: page.title || '',
      icon: page.icon || '',
      link: page.link,
      category: pageCategory,
      displayOrder: page.displayOrder || 0,
      isActive: page.isActive !== undefined ? page.isActive : true
    });
    setShowAddForm(false);
  };

  const resetForm = () => {
    setFormData({
      id: '',
      name: '',
      title: '',
      icon: '',
      link: '',
      category: '',
      displayOrder: 0,
      isActive: true
    });
    setEditingPage(null);
    setShowAddForm(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Фильтрация страниц по категории
  const filteredPages = selectedCategory
    ? Object.entries(pages).reduce((acc, [categoryName, categoryPages]) => {
        if (categoryName === selectedCategory) {
          acc[categoryName] = categoryPages;
        }
        return acc;
      }, {})
    : pages;

  if (!isOpen) return null;

  return (
    <div className={embedded ? 'w-full' : 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto'}>
      <div className={embedded
        ? 'bg-white w-full'
        : 'bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto'}>
        {/* Header */}
        {!embedded && <div className="sticky top-0 z-10 bg-white border-b px-6 py-5 flex justify-between items-center">
          <h2 className={`${embedded ? 'text-xl' : 'text-2xl'} font-bold text-gray-900`}>📄 Управление страницами</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>}

        {/* Content */}
        <div className={embedded ? 'p-4' : 'p-6'}>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {/* Controls */}
          <div className="mb-5 flex flex-wrap items-center gap-2.5">
            <button
              onClick={() => {
                setShowAddForm(true);
                setEditingPage(null);
                resetForm();
              }}
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3.5 text-sm font-medium text-blue-700 transition-colors hover:border-blue-300 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-100"
            >
              <FilePlus2 className="h-4 w-4" />
              Добавить страницу
            </button>

            {/* Filter by category */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              aria-label="Фильтр по категории"
              className="h-10 min-w-[190px] rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-600 focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-100"
            >
              <option value="">Все категории</option>
              {Object.keys(pages).map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>

            <button
              onClick={fetchPages}
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-100"
            >
              <RefreshCw className="h-4 w-4" />
              Обновить
            </button>
          </div>

          {/* Add/Edit Form */}
          {(showAddForm || editingPage) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">
                {editingPage ? 'Редактировать страницу' : 'Добавить новую страницу'}
              </h3>
              <form onSubmit={editingPage ? handleUpdatePage : handleAddPage}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ID страницы *
                    </label>
                    <input
                      type="text"
                      name="id"
                      value={formData.id}
                      onChange={handleInputChange}
                      disabled={!!editingPage}
                      required
                      className="w-full border rounded px-3 py-2 disabled:bg-gray-100"
                      placeholder="my-page"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Название *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full border rounded px-3 py-2"
                      placeholder="Моя страница"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Заголовок
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="w-full border rounded px-3 py-2"
                      placeholder="Заголовок страницы"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Иконка (эмодзи)
                    </label>
                    <input
                      type="text"
                      name="icon"
                      value={formData.icon}
                      onChange={handleInputChange}
                      className="w-full border rounded px-3 py-2"
                      placeholder="🆕"
                      maxLength="4"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Путь (URL) *
                    </label>
                    <input
                      type="text"
                      name="link"
                      value={formData.link}
                      onChange={handleInputChange}
                      required
                      className="w-full border rounded px-3 py-2"
                      placeholder="/my-page"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Категория *
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      required
                      className="w-full border rounded px-3 py-2"
                    >
                      <option value="">Выберите категорию</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Порядок отображения
                    </label>
                    <input
                      type="number"
                      name="displayOrder"
                      value={formData.displayOrder}
                      onChange={handleInputChange}
                      className="w-full border rounded px-3 py-2"
                      min="0"
                    />
                  </div>

                  <div className="flex items-center">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        className="mr-2 w-4 h-4"
                      />
                      <span className="text-sm font-medium text-gray-700">Активна</span>
                    </label>
                  </div>
                </div>

                <div className="mt-4 flex gap-2">
                  <button
                    type="submit"
                    className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700"
                  >
                    {editingPage ? '💾 Сохранить' : '➕ Создать'}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="bg-gray-300 text-gray-700 px-6 py-2 rounded hover:bg-gray-400"
                  >
                    Отмена
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Pages List */}
          {loading ? (
            <div className="text-center py-8">Загрузка...</div>
          ) : (
            <div className="space-y-6">
              {Object.entries(filteredPages).map(([categoryName, categoryPages]) => (
                <div key={categoryName} className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {categoryName} ({categoryPages.length})
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white rounded">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">ID</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Иконка</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Название</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Путь</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Порядок</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Статус</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Действия</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {categoryPages.map((page) => (
                          <tr key={page.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-mono text-gray-600">{page.id}</td>
                            <td className="px-4 py-3 text-lg">{page.icon}</td>
                            <td className="px-4 py-3 text-sm font-medium">{page.name}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{page.link}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{page.displayOrder}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 text-xs rounded ${
                                page.isActive
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {page.isActive ? '✓ Активна' : '✗ Неактивна'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEditPage(page)}
                                  className="text-blue-600 hover:text-blue-800 text-sm"
                                  title="Редактировать"
                                >
                                  ✏️
                                </button>
                                <button
                                  onClick={() => handleDeletePage(page.id)}
                                  className="text-red-600 hover:text-red-800 text-sm"
                                  title="Деактивировать"
                                >
                                  🗑️
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`${embedded ? '' : 'sticky bottom-0'} bg-gray-50 border-t px-6 py-4 flex justify-between items-center`}>
          <div className="text-sm text-gray-600">
            Всего страниц: {Object.values(pages).flat().length}
          </div>
          {!embedded && (
            <button
              onClick={onClose}
              className="bg-gray-200 text-gray-700 px-6 py-2 rounded hover:bg-gray-300"
            >
              Закрыть
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PagesManagementModal;
